// import { updatePreset } from '@primeuix/themes'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import * as fechador from '../../utils/fechador'
import { log } from '../../utils/logger'
import * as storage from '../../utils/storage'
import { STORAGE_KEYS } from '../modelo/storageKeys'
// import { getCustomPreset } from './sysPalette'

export interface SavedState {
    accepted: boolean
    isDark: boolean
    color: string
    showDoc: boolean
    docSize: number
    simulCompact: boolean
}

// ****************************************************************************
// Defaults *******************************************************************
// ****************************************************************************
const SHOW_DOC = false
const DOC_SIZE = 30
const SIMUL_COMPACT = false
const DARK_DEFAULT = true

// 1. Objeto constante con los nombres exactos que acepta PrimeVue en palette('{color}')
export const COLOR = {
    EMERALD: 'emerald',
    BLUE: 'blue',
    RED: 'red',
    NOIR: 'noir' //surface
} as const

export const COLOR_DEFAULT = COLOR.EMERALD

// 2. Tipo derivado: "emerald" | "blue" | "noir"
export type PrimaryColor = (typeof COLOR)[keyof typeof COLOR]

// 3. Array plano de valores permitidos para el Type Guard
export const COLOR_VALUES = Object.values(COLOR)

// 4. Type Guard en runtime
export function isPrimaryColor(value: unknown): value is PrimaryColor {
    return typeof value === 'string' && (COLOR_VALUES as string[]).includes(value)
}

export const colorOptions: Array<{ label: string; value: PrimaryColor; bg: string }> = [
    // Opción A: Usando variables CSS de PrimeVue v4 directamente
    { label: 'Emerald', value: COLOR.EMERALD, bg: 'var(--hh-emerald-500, #10b981)' },
    { label: 'Blue', value: COLOR.BLUE, bg: 'var(--hh-blue-500, #3b82f6)' },
    { label: 'Red', value: COLOR.RED, bg: 'var(--hh-red-500, #ef4444)' }

    // Opción B: Usando tu variable personalizada
    // { label: 'Noir', value: COLOR.NOIR, bg: 'var(--hh-surface-500, #18181b)' }
]

// Sincronizar el <head> del index.html con Vue
const getInitialDarkState = (): boolean => {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.sys.code)
        // log.debug('raw:', raw)
        if (!raw) return DARK_DEFAULT
        const parsed = JSON.parse(raw)
        const isDark = Boolean(parsed?.data?.isDark ?? DARK_DEFAULT)
        // log.debug('isDark:', isDark)
        return isDark
    } catch {
        return DARK_DEFAULT
    }
}

// --- Helpers --------------------------------------------------
function isValidSavedState(x: any): x is SavedState {
    if (!x || typeof x !== 'object' || Array.isArray(x)) return false

    const requiredKeys: (keyof SavedState)[] = [
        'accepted',
        'isDark',
        'color',
        'showDoc',
        'docSize',
        'simulCompact'
    ]

    const keys = Object.keys(x)

    // 1. All required keys must exist
    if (!requiredKeys.every((k) => keys.includes(k))) return false

    // 2. No extra keys allowed
    if (keys.length !== requiredKeys.length) return false

    // 3. Type checks
    return (
        typeof x.accepted === 'boolean' &&
        typeof x.isDark === 'boolean' &&
        isPrimaryColor(x.color) &&
        typeof x.showDoc === 'boolean' &&
        typeof x.docSize === 'number' &&
        typeof x.simulCompact === 'boolean'
    )
}

// --- Store -----------------------------------------------------
export const useSysStore = defineStore('sysStore', () => {
    // 🔥 composables deben ir DENTRO del store

    const date = ref(fechador.MIN_DATE_UTC)

    // State from LocalStorage
    const accepted = ref(false) // show toast first start of app

    const isDark = ref(DARK_DEFAULT)
    const color = ref<PrimaryColor>(COLOR_DEFAULT)

    const showDoc = ref(SHOW_DOC)
    const docSize = ref(DOC_SIZE)
    const simulCompact = ref(SIMUL_COMPACT)

    // State not stored
    const isSysReady = ref(false) // keep system from rendering during its initialization

    // Init
    function initSysStore(): void {
        const parsed = storage.getParsed(STORAGE_KEYS.sys)
        if (parsed) {
            date.value = fechador.fromString(parsed.date)
            const json = parsed.data
            if (isValidSavedState(json)) {
                accepted.value = json.accepted

                isDark.value = getInitialDarkState() // json.isDark // <-- Crucial: no usar 'false' por defecto
                color.value = isPrimaryColor(json.color) ? json.color : COLOR_DEFAULT

                showDoc.value = json.showDoc
                docSize.value = json.docSize
                simulCompact.value = json.simulCompact

                if (!fechador.eqDay(fechador.todayUTC(), date.value)) saveState()
            } else {
                log.warn('invalid saved-stated:', json)
            }
        }

        setDarkLight(isDark.value)
        setColor(color.value)

        // ahora sí, la app puede renderizar
        isSysReady.value = true
    }

    // Actions
    function accept(): void {
        if (accepted.value) return // prevents loops
        accepted.value = true
        log.debug('accept()')
    }

    function saveState(): void {
        const toSave: SavedState = {
            accepted: accepted.value,
            isDark: isDark.value,
            color: color.value,
            showDoc: showDoc.value,
            docSize: Math.round(docSize.value * 10) / 10,
            simulCompact: simulCompact.value
        }
        storage.setJson(STORAGE_KEYS.sys, toSave)
    }

    function setDarkLight(isDark: boolean) {
        if (isDark) {
            document.documentElement.dataset.surface = 'zinc' // new world
            document.documentElement.classList.add('app-dark')
            document.documentElement.style.colorScheme = 'dark'
        } else {
            document.documentElement.dataset.surface = 'slate' // new world
            document.documentElement.classList.remove('app-dark')
            document.documentElement.style.colorScheme = 'light'
        }
    }

    function setColor(color: PrimaryColor) {
        document.documentElement.dataset.primary = color // new world
    }

    watch(isDark, (val) => {
        if (document.startViewTransition) document.startViewTransition(() => setDarkLight(val))
        else setDarkLight(val)
    })

    watch(color, (newColor) => setColor(newColor))

    return {
        initSysStore,
        accept,
        saveState,

        // localStorage
        accepted,

        isDark,
        color,

        showDoc,
        docSize,
        simulCompact,

        // no localStorage
        isSysReady // Ready to render (State of sysStore applied)
    }
})
